import { useFragment } from "@/generated";
import { EditSlideNameDocument, SlideFieldsFragmentDoc } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for updating an existing slide's name.
 *
 * Provides a mutation function to update a slide's name. Apollo's normalized
 * cache automatically updates all queries that reference the slide when the
 * mutation returns the updated data.
 *
 * @returns An object containing:
 *   - editSlideName: Async function to update a slide's name. Takes slide ID and name string, returns the updated slide.
 *   - loading: Boolean indicating if the mutation is in progress
 *
 * @example
 * const { editSlideName, loading } = useEditSlideName();
 * const updated = await editSlideName("slide123", "Updated Slide Name");
 */
export function useEditSlideName() {
  const [editSlideNameMutation, { loading }] = useMutation(EditSlideNameDocument);

  const editSlideName = async (slideId: string, name: string) => {
    return withToast(
      async () => {
        const result = await editSlideNameMutation({
          variables: { slideId, name },
        });

        const maskedSlide = result.data?.editSlideName;
        return maskedSlide ? useFragment(SlideFieldsFragmentDoc, maskedSlide) : undefined;
      },
      {
        loading: "Updating slide name...",
        success: `Successfully updated slide name to "${name}"`,
        error: "Failed to update slide name. Please try again.",
      },
      "editSlideName"
    );
  };

  return {
    editSlideName,
    loading,
  };
}
